/**
 * slash-app (https://github.com/seijiMAN/slash-app) を REST/WebSocket で呼ぶための
 * 薄いクライアント。SLASH DANCE STUDIO のチャット機能だけがここを通る。
 *
 * 認証は admin ログイン時に裏で発行された JWT (localStorage の
 * slashapp_access / slashapp_refresh)。401 を受けたら refresh して 1 回だけ
 * リトライする。
 */

const API_BASE =
  process.env.REACT_APP_SLASHAPP_API_URL || "http://localhost:8000";
const WS_BASE =
  process.env.REACT_APP_SLASHAPP_WS_URL ||
  (API_BASE.startsWith("https://")
    ? API_BASE.replace(/^https:/, "wss:")
    : API_BASE.replace(/^http:/, "ws:"));

// admin 自身の API (slash-app ではなく Django)。bridge 経由でチャット JWT を
// 再発行する /api/slashapp/token/ がここに居る。
const ADMIN_API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

const ACCESS_KEY = "slashapp_access";
const REFRESH_KEY = "slashapp_refresh";
const SUB_ID_KEY = "slashapp_sub_id";

// チャット認証が回復不能 (セッション失効・bridge 障害など) になったことを
// 購読側 (ChatPage) に知らせるイベント。再ログイン導線の表示に使う。
export const SLASHAPP_AUTH_LOST_EVENT = "slashapp:auth-lost";

function notifyAuthLost() {
  if (typeof window !== "undefined" && window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent(SLASHAPP_AUTH_LOST_EVENT));
  }
}

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY);
}

export function setTokens({ access, refresh }) {
  if (access) localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(SUB_ID_KEY);
}

let recoveryPromise = null;

async function doRefresh() {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) return null;
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.access) return null;
    localStorage.setItem(ACCESS_KEY, data.access);
    return data.access;
  } catch (e) {
    console.warn("slashapp refresh failed", e);
    return null;
  }
}

/**
 * Django の admin セッションを使って bridge 経由でチャット JWT を再発行する。
 * refresh が効かない場合 (refresh token 不在 = bridge 導入前ログイン、または
 * refresh token も失効) のフォールバック。admin セッションが生きていれば
 * 再ログインせずトークンを取り直せる。
 *
 * @returns {Promise<string|null>} 新しい access token / 失敗時 null
 */
async function reissueViaBridge() {
  try {
    const res = await fetch(`${ADMIN_API_BASE}/api/slashapp/token/`, {
      method: "POST",
      credentials: "include", // admin セッション cookie を送る
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return null; // 401: セッション失効 / 503: bridge 障害
    const data = await res.json();
    const t = data && data.slashapp;
    if (!t || !t.access || !t.refresh) return null;
    localStorage.setItem(ACCESS_KEY, t.access);
    localStorage.setItem(REFRESH_KEY, t.refresh);
    if (t.sub_id != null) localStorage.setItem(SUB_ID_KEY, String(t.sub_id));
    return t.access;
  } catch (e) {
    console.warn("slashapp bridge re-issue failed", e);
    return null;
  }
}

/** refresh → bridge 再発行 の順で access token の回復を試みる。 */
async function recoverAccessToken() {
  const refreshed = await doRefresh();
  if (refreshed) return refreshed;
  return reissueViaBridge();
}

/** 同時多発の 401 / WS 接続で回復処理が二重に走らないよう in-flight を共有。 */
function getRecovery() {
  if (!recoveryPromise) {
    recoveryPromise = recoverAccessToken().finally(() => {
      recoveryPromise = null;
    });
  }
  return recoveryPromise;
}

/**
 * チャット系の通信前に access token があることを保証する。無ければ回復を試み、
 * それでもダメなら auth-lost を通知して null を返す (WebSocket 接続前に使う)。
 * @returns {Promise<string|null>}
 */
export async function ensureSlashAppTokens() {
  const existing = getAccessToken();
  if (existing) return existing;
  const recovered = await getRecovery();
  if (!recovered) {
    clearTokens();
    notifyAuthLost();
  }
  return recovered;
}

/**
 * Authorization ヘッダ自動付与 + 401 リフレッシュ付きの fetch。
 *
 * @param {string} path  ``/api/v1/chat/...`` を ``/api`` 込みで渡す
 * @param {RequestInit} init
 * @returns {Promise<Response>}
 */
export async function slashAppFetch(path, init = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const headers = new Headers(init.headers || {});
  if (!(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const access = getAccessToken();
  if (access) headers.set("Authorization", `Bearer ${access}`);

  let res = await fetch(url, { ...init, headers });
  if (res.status !== 401) return res;

  // 401 → 回復試行 (refresh → bridge 再発行。in-flight を共有)
  const next = await getRecovery();
  if (!next) {
    clearTokens();
    notifyAuthLost(); // セッション失効など。ChatPage が再ログインを促す
    return res;
  }
  headers.set("Authorization", `Bearer ${next}`);
  res = await fetch(url, { ...init, headers });
  return res;
}

/**
 * 認証ヘッダも 401 リフレッシュも付けない素の JSON フェッチ。共有リンク（?key=）で
 * 未ログインのまま叩く読み取り専用エンドポイント向け（Authorization を付けると
 * 401→bridge 再発行の無駄打ちが走るため専用に分ける）。
 */
export async function slashAppPublicJson(path, init = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, init);
  if (!res.ok) {
    const text = await res.text();
    let detail = text;
    try {
      detail = JSON.parse(text).detail || text;
    } catch (_) {}
    const err = new Error(`HTTP ${res.status}: ${detail}`);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

export async function slashAppJson(path, init = {}) {
  const res = await slashAppFetch(path, init);
  if (!res.ok) {
    const text = await res.text();
    let detail = text;
    try {
      detail = JSON.parse(text).detail || text;
    } catch (_) {}
    const err = new Error(`HTTP ${res.status}: ${detail}`);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

/**
 * Open a WebSocket toward slash-app. JWT は ``?token=...`` で渡す
 * (apps.chat.ws_auth.JWTAuthMiddleware が読む)。
 */
export function openSlashAppSocket(path) {
  const access = getAccessToken() || "";
  const sep = path.includes("?") ? "&" : "?";
  return new WebSocket(
    `${WS_BASE}${path}${sep}token=${encodeURIComponent(access)}`
  );
}
